/* ─────────────────────────────────────────────────────────────────────
   BY BANOO — Homepage JS
   Vanilla JS only. Handles: carousel, quick-add, scroll behaviours.
───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════
     Carousel
  ═══════════════════════════════════════════ */

  /**
   * BBCarousel
   * @param {HTMLElement} outerEl  .bb-carousel-outer wrapper
   */
  class BBCarousel {
    constructor(outerEl) {
      this.outer    = outerEl;
      this.track    = outerEl.querySelector('.bb-carousel');
      this.prevBtn  = outerEl.querySelector('[data-carousel-prev]');
      this.nextBtn  = outerEl.querySelector('[data-carousel-next]');

      if (!this.track) return;

      this._touchStartX = 0;
      this._touchStartY = 0;
      this._isDragging  = false;
      this._startScrollLeft = 0;

      this._bindArrows();
      this._bindTouch();
      this._bindScroll();
      this._updateArrows();
    }

    /* ── Arrow buttons ── */
    _bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._scroll(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._scroll(1));
      }
    }

    _scroll(direction) {
      const items    = this.track.querySelectorAll('.bb-carousel__item');
      if (!items.length) return;
      const itemW    = items[0].offsetWidth;
      const gap      = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step     = (itemW + gap);
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    _bindScroll() {
      this.track.addEventListener('scroll', () => this._updateArrows(), { passive: true });
      // Also update on resize
      if (typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => this._updateArrows());
        this._ro.observe(this.track);
      }
    }

    _updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.prevBtn.disabled = scrollLeft <= 2;
      this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
    }

    /* ── Touch / swipe ── */
    _bindTouch() {
      const el = this.track;

      el.addEventListener('touchstart', (e) => {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._startScrollLeft = el.scrollLeft;
        this._isDragging = true;
      }, { passive: true });

      el.addEventListener('touchmove', (e) => {
        if (!this._isDragging) return;
        const dx = this._touchStartX - e.touches[0].clientX;
        const dy = this._touchStartY - e.touches[0].clientY;
        // If mostly horizontal, let the carousel handle it natively (scroll-snap)
        // If mostly vertical, prevent carousel scroll
        if (Math.abs(dy) > Math.abs(dx)) {
          this._isDragging = false;
        }
      }, { passive: true });

      el.addEventListener('touchend', () => {
        this._isDragging = false;
        this._updateArrows();
      }, { passive: true });

      /* Mouse drag (desktop) */
      el.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        this._isDragging = true;
        this._touchStartX = e.clientX;
        this._startScrollLeft = el.scrollLeft;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      });

      window.addEventListener('mousemove', (e) => {
        if (!this._isDragging) return;
        const dx = this._touchStartX - e.clientX;
        el.scrollLeft = this._startScrollLeft + dx;
      });

      window.addEventListener('mouseup', () => {
        if (!this._isDragging) return;
        this._isDragging = false;
        el.style.cursor = '';
        el.style.userSelect = '';
        this._updateArrows();
      });
    }

    destroy() {
      if (this._ro) this._ro.disconnect();
    }
  }

  /* ═══════════════════════════════════════════
     Quick Add to Cart
  ═══════════════════════════════════════════ */

  /**
   * Add a single variant to the Shopify cart via AJAX.
   * @param {string|number} variantId
   * @param {number}        quantity
   * @returns {Promise<Response>}
   */
  async function cartAdd(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  /**
   * Update the cart count bubble in the header (works with Minimog theme).
   */
  async function refreshCartCount() {
    try {
      const res  = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const data = await res.json();
      const count = data.item_count;
      // Minimog theme selectors
      document.querySelectorAll('[data-cart-count], .m-nav__cart-count, .header-cart-count').forEach(el => {
        el.textContent = count;
        el.classList.toggle('is-hidden', count === 0);
      });
    } catch (e) { /* silent */ }
  }

  /**
   * Dispatch a standard Shopify cart:add event (picked up by cart drawers).
   */
  function dispatchCartEvent(item) {
    document.dispatchEvent(new CustomEvent('cart:add', { detail: { item } }));
    // Minimog theme also listens to this:
    document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { item } }));
  }

  /** Handle quick-add button clicks */
  function handleQuickAdd(btn) {
    const variantId = btn.dataset.variantId;
    const productUrl = btn.dataset.productUrl;

    if (!variantId) {
      // Has variants → go to product page
      if (productUrl) window.location.href = productUrl;
      return;
    }

    btn.classList.add('is-loading');
    const originalText = btn.textContent.trim();
    btn.setAttribute('aria-disabled', 'true');

    cartAdd(variantId)
      .then((item) => {
        btn.textContent = 'Added';
        refreshCartCount();
        dispatchCartEvent(item);
        const cd = document.querySelector('m-cart-drawer');
        if (cd) {
          cd.open();
          cd.onCartDrawerUpdate();
        }
        setTimeout(() => {
          btn.textContent = originalText;
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-loading');
        }, 1400);
      })
      .catch(() => {
        btn.textContent = 'Error';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-loading');
        }, 1400);
      });
  }

  /* ═══════════════════════════════════════════
     Newsletter form
  ═══════════════════════════════════════════ */
  function initNewsletterForm(form) {
    if (!form || form.dataset.bbInited) return;
    form.dataset.bbInited = '1';

    const msgEl = form.querySelector('.bb-newsletter__message');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const emailInput = form.querySelector('[type="email"]');
      if (!emailInput || !emailInput.value) return;

      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) submitBtn.disabled = true;

      try {
        const fd = new FormData(form);
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd
        });

        if (res.ok || res.status === 422) {
          const data = await res.json().catch(() => ({}));
          if (data.errors) {
            if (msgEl) {
              msgEl.textContent = Object.values(data.errors).flat().join(' ');
              msgEl.className = 'bb-newsletter__message bb-newsletter__message--error';
            }
          } else {
            if (msgEl) {
              msgEl.textContent = 'Thank you for subscribing.';
              msgEl.className = 'bb-newsletter__message bb-newsletter__message--success';
            }
            emailInput.value = '';
          }
        } else {
          throw new Error('Network error');
        }
      } catch {
        if (msgEl) {
          msgEl.textContent = 'Something went wrong. Please try again.';
          msgEl.className = 'bb-newsletter__message bb-newsletter__message--error';
        }
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    });
  }

  /* ═══════════════════════════════════════════
     Init helpers
  ═══════════════════════════════════════════ */

  function initCarousels(scope) {
    const root = scope || document;
    root.querySelectorAll('.bb-carousel-outer:not([data-bb-inited])').forEach(el => {
      el.dataset.bbInited = '1';
      new BBCarousel(el);
    });
  }

  let cardTouchBound = false;

  function initQuickAdd(scope) {
    const root = scope || document;

    if (window.matchMedia('(pointer: coarse)').matches) {
      if (!cardTouchBound) {
        cardTouchBound = true;
        document.addEventListener('click', function(e) {
          if (!e.target.closest('.bb-card')) {
            document.querySelectorAll('.bb-card.is-open').forEach(function(c) {
              c.classList.remove('is-open');
            });
          }
        });
      }
      root.querySelectorAll('.bb-card:not([data-bb-touch])').forEach(function(card) {
        card.dataset.bbTouch = '1';
        card.addEventListener('click', function(e) {
          if (e.target.closest('[data-bb-quick-add]')) return;
          // Link taps (image / title) navigate immediately; CTA reveals as side effect
          if (e.target.closest('a')) {
            card.classList.add('is-open');
            return;
          }
          if (card.classList.contains('is-open')) return;
          document.querySelectorAll('.bb-card.is-open').forEach(function(c) {
            c.classList.remove('is-open');
          });
          card.classList.add('is-open');
          e.preventDefault();
        });
      });
    }

    root.querySelectorAll('[data-bb-quick-add]:not([data-bb-inited])').forEach(btn => {
      btn.dataset.bbInited = '1';
      btn.addEventListener('click', () => handleQuickAdd(btn));
    });
  }

  function initNewsletterForms(scope) {
    const root = scope || document;
    root.querySelectorAll('.bb-newsletter__form').forEach(initNewsletterForm);
  }

  /** Card image carousel — prev/next arrows cycle through product images */
  function initCardCarousels(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-bb-card-slides]:not([data-bb-card-inited])').forEach(function(wrap) {
      const slides = wrap.querySelectorAll('.bb-card__slide');
      if (slides.length < 2) return;
      wrap.dataset.bbCardInited = '1';

      let current = 0;

      function show(idx) {
        slides[current].classList.remove('is-active');
        current = (idx + slides.length) % slides.length;
        slides[current].classList.add('is-active');
      }

      const prevBtn = wrap.querySelector('.bb-card__arrow--prev');
      const nextBtn = wrap.querySelector('.bb-card__arrow--next');

      if (prevBtn) prevBtn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        show(current - 1);
      });

      if (nextBtn) nextBtn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        show(current + 1);
      });
    });
  }

  function initJudgeMeBadges(scope) {
    const root = scope || document;
    root.querySelectorAll('.bb-card__stars .jdgm--loading').forEach(el => {
      el.classList.remove('jdgm--loading');
    });
  }

  function initAll(scope) {
    initCarousels(scope);
    initQuickAdd(scope);
    initCardCarousels(scope);
    initNewsletterForms(scope);
    initJudgeMeBadges(scope);
    if (!window._bbCardObserver) {
      window._bbCardObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(m) {
          m.addedNodes.forEach(function(node) {
            if (node.nodeType !== 1) return;
            if (node.querySelector && node.querySelector('.bb-card')) {
              initQuickAdd(node);
              initCardCarousels(node);
            }
          });
        });
      });
      window._bbCardObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ═══════════════════════════════════════════
     Shopify Theme Editor live-reload support
  ═══════════════════════════════════════════ */
  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });

  /* ═══════════════════════════════════════════
     DOM ready
  ═══════════════════════════════════════════ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Expose for debugging / external use */
  window.BBHomepage = { initAll, initCarousels, initQuickAdd, cartAdd };

})();
